import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Loader2, MessageSquareWarning, Pencil, Trash2 } from "lucide-react";
import ListingCard from "@/components/ListingCard";
import { api, formatApiError, STATUS_META } from "@/lib/api";
import { PageLoading } from "@/App";
import Reveal from "@/components/Reveal";
import { toast } from "sonner";

export default function MyListings() {
  const [listings, setListings] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => {
    api
      .get("/my/listings")
      .then((r) => setListings(r.data))
      .catch(() => setListings([]));
  };
  useEffect(load, []);

  const remove = async (id) => {
    if (!window.confirm("Bu ilanı kalıcı olarak silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/listings/${id}`);
      toast.success("İlan silindi.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (listings === null) return <PageLoading />;

  const filtered = filter === "all" ? listings : listings.filter((l) => l.status === filter);
  const counts = { all: listings.length };
  ["pending", "approved", "revision", "rejected"].forEach((s) => {
    counts[s] = listings.filter((l) => l.status === s).length;
  });

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-24 pb-20">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-mono text-xs font-bold tracking-widest text-coral">HESABIM</p>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight mt-1">İlanlarım</h1>
          <p className="mt-2 text-sm text-slate-500">İlanlarınızın onay durumunu buradan takip edin.</p>
        </div>
        <Link data-testid="my-listings-create-button" to="/ilan-ver" className="inline-flex items-center gap-2 rounded-full bg-coral px-6 py-3 text-sm font-bold text-white hover:bg-coraldark transition shadow-lg shadow-coral/25">
          <Plus size={17} strokeWidth={3} /> Yeni İlan
        </Link>
      </div>

      <div className="mt-7 flex gap-2 overflow-x-auto no-scrollbar" data-testid="my-listings-filter-row">
        {[["all", "Tümü"], ["pending", "Onay Bekleyen"], ["approved", "Yayında"], ["revision", "Revizyon"], ["rejected", "Reddedilen"]].map(([k, label]) => (
          <button
            key={k}
            data-testid={`my-listings-filter-${k}`}
            onClick={() => setFilter(k)}
            className={`shrink-0 rounded-full border px-4 py-2 text-sm font-bold transition ${
              filter === k ? "bg-coal text-bone border-coal" : "border-coal/15 bg-card hover:border-coral hover:text-coral"
            }`}
          >
            {label} <span className="font-mono text-xs opacity-60">({counts[k]})</span>
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="mt-10 rounded-3xl border-2 border-dashed border-coal/15 py-20 text-center" data-testid="my-listings-empty-state">
          <p className="font-display text-xl font-bold">Bu durumda ilanınız yok</p>
          <p className="mt-2 text-sm text-slate-500">Yeni bir ilan vererek başlayın — tamamen ücretsiz.</p>
          <Link to="/ilan-ver" className="mt-6 inline-block rounded-full bg-coral px-6 py-3 text-sm font-bold text-white hover:bg-coraldark transition">İlan Ver</Link>
        </div>
      ) : (
        <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-3" data-testid="my-listings-grid">
          {filtered.map((l, i) => (
            <Reveal key={l.id} delay={Math.min(i * 0.04, 0.3)}>
              <div className="relative">
                <ListingCard listing={l} showStatus />
                {(l.status === "revision" || l.status === "rejected") && l.feedback && (
                  <div data-testid={`listing-feedback-${l.id}`} className="mt-2 flex items-start gap-2 rounded-xl border border-orange-200 bg-orange-50 px-3.5 py-2.5 text-xs leading-relaxed text-orange-900">
                    <MessageSquareWarning size={14} className="shrink-0 mt-0.5" />
                    <span><strong>Ekip geri dönüşü:</strong> {l.feedback}</span>
                  </div>
                )}
                <div className="mt-2 flex gap-2">
                  {(l.status === "revision" || l.status === "rejected" || l.status === "pending") && (
                    <Link
                      data-testid={`edit-listing-${l.id}`}
                      to={`/ilan-ver/edit/${l.id}`}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-coal/15 px-4 py-2 text-xs font-bold hover:border-coral hover:text-coral transition"
                    >
                      <Pencil size={13} /> {l.status === "approved" ? "Düzenle" : "Güncelle & Tekrar Gönder"}
                    </Link>
                  )}
                  <button
                    data-testid={`delete-listing-${l.id}`}
                    onClick={() => remove(l.id)}
                    className="inline-flex items-center justify-center gap-1.5 rounded-full border border-red-200 px-4 py-2 text-xs font-bold text-red-600 hover:bg-red-50 transition"
                  >
                    <Trash2 size={13} /> Sil
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
}
